// src/pages/Coordenacao/embarcacoesCadastradasData.ts
import { useEffect, useRef, useState } from "react";

/** ---------------------- Tipos ---------------------- */
export type EmbarcacaoClienteAPI = {
  id: number;
  nome_embarcacao: string;
  nome_empresa: string;
  // empresa_id existe no backend, mas vamos ignorar por enquanto
};

type RespAPI = {
  ok: boolean;
  meta: any;
  data: EmbarcacaoClienteAPI[];
};

/** ---------------------- Constantes ---------------------- */
const URL = `https://api.eva-googlemarine.com/departamentos/coordenacao/clientes-embarcacoes`;

/** ---------------------- Fetch simples ---------------------- */
export async function fetchEmbarcacoesClientesOnce(): Promise<{
  data: EmbarcacaoClienteAPI[];
  meta: RespAPI["meta"] | null;
}> {
  const res = await fetch(URL, {
    method: "GET",
    headers: { Accept: "application/json" },
    cache: "no-store",
  });

  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }

  const json = (await res.json()) as RespAPI;

  if (!json?.ok) {
    return { data: [], meta: json?.meta ?? null };
  }

  const data = Array.isArray(json.data) ? json.data : [];
  return { data, meta: json.meta ?? null };
}

/** ---------------------- Hook ---------------------- */
export function useEmbarcacoesClientes() {
  const [data, setData] = useState<EmbarcacaoClienteAPI[]>([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState<RespAPI["meta"] | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const fetching = useRef(false);
  const stop = useRef(false);

  async function carregar() {
    if (fetching.current) return;
    fetching.current = true;
    try {
      setError(null);
      const r = await fetchEmbarcacoesClientesOnce();
      setData(r.data);
      setMeta(r.meta);
    } catch (e: any) {
      console.error("Erro em fetchEmbarcacoesClientesOnce:", e);
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      fetching.current = false;
      setLoading(false);
    }
  }

  useEffect(() => {
    stop.current = false;
    carregar();

    return () => {
      stop.current = true;
    };
  }, []);

  const refresh = async () => {
    if (fetching.current) return;
    await carregar();
  };

  return { data, loading, error, refresh, meta };
}
